/**
 * TrackerOrderTable
 *
 * Flat order list for the Production Tracker "Orders" view.
 * Each row shows order info + batch reference; clicking expands
 * to show garment items via TrackerGarmentTable.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import {
  useTrackerClientOrg,
  type TrackerClientOrg,
} from '../hooks/useTrackerClientOrg';
import { formatOrderNumber, type PortlessFulfilmentType } from '@threadheads/shared';
import { hybridBadge } from '@/lib/portless-fulfilment-display';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronRight, MoreHorizontal, ExternalLink, Check, Clock, Activity, Sliders, Receipt, Printer, Pause } from 'lucide-react';
import { formatDistanceToNow, format, differenceInHours } from 'date-fns';
import { cn } from '@/lib/utils';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { SplitBadge } from '@/components/SplitBadge';
import { usePrintAgent } from '@/features/print-station/hooks/usePrintAgent';
import { ExpandedOrderContent } from './ExpandedOrderContent';
import { StatusOverrideDialog, type OverrideTarget } from './StatusOverrideDialog';
import {
  STATUS_LABEL_KEYS,
  statusColors,
  getFulfillmentType,
  getAgeColor,
} from '../lib/order-status-display';

// ============================================================================
// TYPES
// ============================================================================

interface TrackerOrder {
  id: string;
  order_name: string;
  customer_name: string | null;
  shopify_order_id: number | null;
  batch_id: string;
  batch_number: number;
  bin_label: string | null;
  production_status: string;
  display_fulfillment_status: string | null;
  total_pod_items: number;
  total_pap_items: number;
  items_picked: number;
  items_caught: number;
  has_redo_pending: boolean;
  created_at: string;
  updated_at: string;
  /**
   * BLU-367: the order's OWN org — at a shared 3PL location this is the CLIENT
   * org, not the caller's. Optional/nullable for the same reason as
   * `fulfilment_type`: a react-query cache written by the previous deploy has
   * no such field, and the fallback (no chip) beats a wrong attribution.
   */
  organization_id?: string | null;
  /**
   * BLU-783: does this row's batch belong to the caller's own org? On a 3PL
   * floor it usually does not — the Orders list is scoped by location, but the
   * batch-detail read is still caller-org scoped, so "View batch" would 404.
   *
   * Optional/nullable for the same reason as `organization_id`: a react-query
   * cache written by the previous deploy has no such field. The guard below
   * acts only on an explicit `false`, so absent/null keeps the old behaviour.
   */
  batch_is_own?: boolean | null;
  /**
   * Hybrid vs Cross Dock, computed server-side by the shared rule. Optional so a
   * stale cached payload renders no badge rather than a wrong one.
   */
  fulfilment_type?: PortlessFulfilmentType;
  /**
   * BLU-758: how many LIVE fulfilment-order parts this ERP order has (this row
   * plus its siblings). ≥2 ⇒ split. Optional for the same reason as
   * `fulfilment_type`: a react-query cache written by the previous deploy has
   * neither field, and the fallback (1) renders no badge rather than a wrong one.
   */
  split_total?: number;
  /** BLU-758: true when every OTHER live part has already shipped. */
  siblings_all_terminal?: boolean;
  /** BLU-835 active parcel summary (first tracking + additional count). */
  package_tracking_number?: string | null;
  package_count?: number;
}

interface TrackerOrderTableProps {
  orders: TrackerOrder[];
  shopifyDomain?: string | null;
  isLoading?: boolean;
  locationId?: string;
  /** Whether invoice printing is enabled for this location (BLU-328) */
  invoiceEnabled?: boolean;
  /** Callback to print invoice for a single order (BLU-328) */
  onPrintOrderInvoice?: (orderId: string) => void;
  /** Put an in-production order on hold (CX/Warehouse Staff self-service, BLU-367). */
  onPutOnHold?: (order: { id: string }) => void;
  /** BLU-1462: a client QC redo reopened this order (switch to Active + search it). */
  onClientQcRedoDone?: (orderName: string) => void;
}

// STATUS_LABEL_KEYS / statusColors / getFulfillmentType / getAgeColor moved to
// ../lib/order-status-display (BLU-758) — the extracted ExpandedOrderContent
// needs the same vocabulary and is imported by THIS file, so keeping them here
// would have made the import graph cyclic. Import from the lib module.

/**
 * Mirrors holdProdOrder's status denylist (`production-issues.dao.ts`) — the
 * server is the real gate, this only stops us offering a button that always
 * CONFLICTs. It must be the FULL list: this table renders the Shipped tab too
 * (`terminalOnly`), whose rows are all terminal, so a PACKED-only check offered
 * the action on every shipped order.
 */
export const NON_HOLDABLE_ORDER_STATUSES: ReadonlySet<string> = new Set([
  'PACKED',
  'SHIPPED',
  'EXTERNALLY_FULFILLED',
  'CANCELLED',
  'READY_FOR_PICKUP',
  'ON_HOLD',
]);

// ============================================================================
// AGE INDICATOR (for tooltip)
// ============================================================================

function getUpdateColor(updatedAt: string): string {
  const hours = differenceInHours(new Date(), new Date(updatedAt));
  if (hours < 2) return 'text-green-600 dark:text-green-400';
  if (hours < 8) return 'text-muted-foreground';
  if (hours < 24) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// ============================================================================
// PROGRESS BAR (status-aware)
// ============================================================================

function ProgressBar({ picked, caught, total, status, t }: { picked: number; caught: number; total: number; status: string; t: (key: string) => string }) {
  if (total === 0) return <span className="text-xs text-muted-foreground">-</span>;

  // Shipped/Ready for Pickup → solid green bar with checkmark
  if (status === 'SHIPPED' || status === 'READY_FOR_PICKUP') {
    return (
      <div className="flex items-center gap-2 min-w-[100px]">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full w-full" />
        </div>
        <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
      </div>
    );
  }

  // Externally fulfilled → gray text, no bar
  if (status === 'EXTERNALLY_FULFILLED') {
    return (
      <span className="text-xs text-muted-foreground italic">{t('tracker.orders.status.extFulfilled')}</span>
    );
  }

  // Cancelled → dash
  if (status === 'CANCELLED' || status === 'ON_HOLD') {
    // Paused/terminal — no production progress to show
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  // Normal progress bar
  const pickedPct = Math.round((picked / total) * 100);
  const caughtPct = Math.round((caught / total) * 100);

  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="relative h-full">
          <div
            className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all"
            style={{ width: `${caughtPct}%` }}
          />
          {pickedPct > caughtPct && (
            <div
              className="absolute inset-y-0 bg-blue-400 rounded-full transition-all"
              style={{ left: `${caughtPct}%`, width: `${pickedPct - caughtPct}%` }}
            />
          )}
        </div>
      </div>
      <span className="text-[11px] tabular-nums text-muted-foreground whitespace-nowrap">
        {caught}/{total}
      </span>
    </div>
  );
}

// ============================================================================
// ORDER ROW + EXPANSION
// ============================================================================

function OrderRowGroup({
  order,
  isExpanded,
  onToggle,
  shopifyDomain,
  hasOverrideAccess,
  onOverrideStatus,
  locationId: tableLocationId,
  invoiceEnabled,
  onPrintOrderInvoice,
  onPutOnHold,
  canReprintShippingLabel,
  onReprintShippingLabel,
  clientOrg,
  onClientQcRedoDone,
}: {
  order: TrackerOrder;
  clientOrg: TrackerClientOrg;
  isExpanded: boolean;
  onToggle: () => void;
  shopifyDomain?: string | null;
  hasOverrideAccess?: boolean;
  onOverrideStatus?: (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => void;
  locationId?: string;
  invoiceEnabled?: boolean;
  onPrintOrderInvoice?: (orderId: string) => void;
  onPutOnHold?: (order: { id: string }) => void;
  canReprintShippingLabel?: boolean;
  onReprintShippingLabel?: (orderId: string) => void;
  onClientQcRedoDone?: (orderName: string) => void;
}) {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const totalItems = order.total_pod_items + order.total_pap_items;
  const shopifyUrl = getShopifyAdminOrderUrl(shopifyDomain, order.shopify_order_id);
  // BLU-783: batch detail is caller-org scoped; this row's batch may not be.
  // Explicit `false` only — absent/null means a pre-BLU-783 cached payload.
  const canOpenBatch = order.batch_is_own !== false;
  const fulfillment = getFulfillmentType(order.total_pod_items, order.total_pap_items);
  const hybrid = order.fulfilment_type ? hybridBadge(order.fulfilment_type) : null;
  const ageColor = getAgeColor(order.created_at);
  const ageText = formatDistanceToNow(new Date(order.created_at), { addSuffix: true });
  const updateColor = getUpdateColor(order.updated_at);
  const updateText = formatDistanceToNow(new Date(order.updated_at), { addSuffix: true });
  // BLU-758: split provenance. The Items count on a split row is ONE fulfilment
  // order's worth, never the whole order — unexplained, that shortfall reads as
  // a picking error.
  const splitTotal = order.split_total ?? 1;
  const isSplit = splitTotal >= 2;
  // Three-way on purpose, not truthiness: `undefined` means the row came from a
  // list payload that predates this field (a stale react-query cache after a
  // deploy), NOT "the siblings are still in production". Falsy-collapsing the
  // two makes the badge assert a sibling state nobody sent — and "rest in
  // production" is the claim that keeps an operator waiting for a parcel that
  // already shipped. Unknown ⇒ say "Split" and stop talking.
  const splitState =
    order.siblings_all_terminal === true
      ? 'rest_shipped'
      : order.siblings_all_terminal === false
        ? 'in_production'
        : undefined;
  // The row's accessible name replaces its contents for a screen reader, so the
  // badge cluster has to be spelled out here or it is inaudible — and it has to
  // claim exactly what the pill claims, including when the state is unknown.
  const splitAriaKey =
    splitState === 'rest_shipped'
      ? 'tracker.orders.split.ariaRestShipped'
      : splitState === 'in_production'
        ? 'tracker.orders.split.ariaRestInProduction'
        : 'tracker.orders.split.ariaParts';
  const ariaLabel = [
    t('tracker.orders.rowAria', { order: order.order_name, items: totalItems }),
    isSplit ? t(splitAriaKey, { parts: splitTotal }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors group',
          isExpanded && 'bg-muted/30'
        )}
        onClick={onToggle}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell className="w-10 px-3">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </TableCell>
        <TableCell className="font-medium">
          {/* flex-wrap: this cluster can now carry three pills (Redo, Split,
              Hybrid). Without it the column widens and squeezes every other
              column on a laptop; with it, the pills drop to a second line. */}
          <div className="flex flex-wrap items-center gap-2">
            {shopifyUrl ? (
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {formatOrderNumber(order.order_name)}
              </a>
            ) : (
              <span>{formatOrderNumber(order.order_name)}</span>
            )}
            {order.has_redo_pending && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 dark:text-amber-400">
                Redo
              </Badge>
            )}
            {/* BLU-758: split provenance, ordered Redo → Split → Hybrid —
                action-needed first, then why this row is short, then how the
                parcel ships. Same pill as the pre-batch Order Queue
                (<SplitBadge>), with the sibling roll-up the queue can't know.
                The per-part detail is in the expanded row, not a tooltip:
                PRODUCTION_DESIGN.md §10. */}
            {isSplit && <SplitBadge count={splitTotal} state={splitState} />}
            {/* BLU-367: whose order this is. Gated on isCrossOrg — own-org rows
                must render nothing, or every row at a single-tenant location
                gets a meaningless badge. */}
            {clientOrg.isCrossOrg(order.organization_id) && (
              <ClientOrgChip label={clientOrg.labelFor(order.organization_id)} />
            )}
            {/* Hybrid only — Cross Dock is the default and gets no badge. A Hybrid
                parcel is deliberately short here (Portless adds its own half), so
                the Items count on this row is not the whole order.

                Glyph + label, no title attribute: the Type column two cells away
                renders POD in the same blue (getFulfillmentType), and a tablet has
                no hover to disambiguate them — PRODUCTION_DESIGN.md §10. A table
                cell has no room for the sentence, so the label is the whole message
                here; the sentence is a visible callout on the expanded row of a
                batch (TrackerOrderRow) and on the packing slip. */}
            {hybrid && (
              <Badge variant={hybrid.variant} size="sm" className="shrink-0 whitespace-nowrap">
                <hybrid.icon className="h-3 w-3" aria-hidden="true" />
                {t(hybrid.labelKey)}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {order.customer_name ?? '-'}
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className={fulfillment.className}>
            {t(fulfillment.labelKey)}
          </Badge>
        </TableCell>
        <TableCell>
          <span className="text-sm text-muted-foreground font-mono">
            BATCH-{String(order.batch_number).padStart(3, '0')}
          </span>
        </TableCell>
        <TableCell>
          {order.bin_label ? (
            <Badge variant="outline" className="font-mono text-xs">
              {order.bin_label}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={cn('inline-flex items-center gap-1 text-xs cursor-default', ageColor)} onClick={(e) => e.stopPropagation()}>
                <Clock className="h-3.5 w-3.5" />
                {ageText.replace(' ago', '')}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {format(new Date(order.created_at), 'PPp')}
            </TooltipContent>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={cn('inline-flex items-center gap-1 text-xs cursor-default whitespace-nowrap', updateColor)} onClick={(e) => e.stopPropagation()}>
                <Activity className="h-3.5 w-3.5" />
                {updateText.replace(' ago', '')}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {format(new Date(order.updated_at), 'PPp')}
            </TooltipContent>
          </Tooltip>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {totalItems}
        </TableCell>
        <TableCell>
          <ProgressBar
            picked={order.items_picked}
            caught={order.items_caught}
            total={totalItems}
            status={order.production_status}
            t={t}
          />
        </TableCell>
        <TableCell>
          <Badge
            variant="secondary"
            className={statusColors[order.production_status] ?? statusColors.NEW}
          >
            {STATUS_LABEL_KEYS[order.production_status] ? t(STATUS_LABEL_KEYS[order.production_status]!) : order.production_status.replace(/_/g, ' ')}
          </Badge>
        </TableCell>
        <TableCell>
          {order.package_tracking_number ? (
            <span className="font-mono text-xs break-all">
              {order.package_tracking_number}
              {(order.package_count ?? 0) > 1 && (
                <Badge variant="outline" className="ml-1.5 px-1 py-0 text-[10px] font-sans">
                  +{(order.package_count ?? 1) - 1}
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('tracker.orders.orderActions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {shopifyUrl && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(shopifyUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('tracker.orders.viewInShopify')}
                </DropdownMenuItem>
              )}
              {/* BLU-783: hidden when the batch belongs to another org — batch
                  detail stays caller-org scoped, so it would 404. */}
              {canOpenBatch && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/production/batches/${order.batch_id}`);
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('tracker.orders.viewBatch')}
                </DropdownMenuItem>
              )}
              {/* Keep-in-bins pause: the garments wait where they are and the order
                  moves to Issues > On hold. No skip-hold twin as on the queue tab —
                  once held, the row leaves this tab entirely. */}
              {onPutOnHold && !NON_HOLDABLE_ORDER_STATUSES.has(order.production_status) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onPutOnHold(order);
                    }}
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    {t('orders.actions.putOnHold')}
                  </DropdownMenuItem>
                </>
              )}
              {invoiceEnabled && onPrintOrderInvoice && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrintOrderInvoice(order.id);
                    }}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    {t('tracker.invoice.printInvoice')}
                  </DropdownMenuItem>
                </>
              )}
              {canReprintShippingLabel && onReprintShippingLabel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onReprintShippingLabel(order.id);
                    }}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {t('tracker.orders.reprintShippingLabel')}
                  </DropdownMenuItem>
                </>
              )}
              {hasOverrideAccess && onOverrideStatus && tableLocationId && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onOverrideStatus({
                        orderId: order.id,
                        orderName: order.order_name,
                        currentStatus: order.production_status,
                        locationId: tableLocationId,
                      });
                    }}
                  >
                    <Sliders className="h-4 w-4 mr-2" />
                    {t('tracker.orders.overrideStatus')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {/* Expanded garments — rendered directly after this order row */}
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={13} className="p-0">
              <motion.div
                initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, height: 'auto' }}
                exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-muted/10 border-b"
              >
                <ExpandedOrderContent
                  orderId={order.id}
                  orderName={order.order_name}
                  expectSiblings={isSplit}
                  onClientQcRedoDone={onClientQcRedoDone}
                />
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TrackerOrderTable({ orders, shopifyDomain, isLoading, locationId, invoiceEnabled, onPrintOrderInvoice, onPutOnHold, onClientQcRedoDone }: TrackerOrderTableProps) {
  // Resolved once here, not per row — one query subscription instead of one
  // per rendered order.
  const clientOrg = useTrackerClientOrg();
  const { t } = useTranslation('production');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const { isAdmin, isWarehouseCoordinator, isPacker, can } = useRole();
  // BLU-842: read the permission, not a role list — CX holds it too, and the
  // backend gates on exactly this permission (four-layer alignment).
  const hasOverrideAccess = can('orders.status_override');
  // Mirrors shipping.handler.ts's enforceShippingPolicy (Admin/Coordinator/
  // Packer) — the backend re-checks this, but gating the menu item too
  // avoids showing an action that will always be rejected server-side.
  const canReprintShippingLabel = isAdmin() || isWarehouseCoordinator() || isPacker();
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null);
  const { printLabel: agentPrintLabel } = usePrintAgent();
  const reprintShippingLabelMutation = trpc.shipping.reprintShippingLabel.useMutation();

  const toggleExpand = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleOverrideStatus = (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => {
    setOverrideTarget({ type: 'order', ...target });
  };

  const handleReprintShippingLabel = useCallback((orderId: string) => {
    if (!locationId) return;
    reprintShippingLabelMutation.mutate(
      { orderId, locationId },
      {
        onSuccess: async (result) => {
          if (result.dashboardUrl) {
            window.open(result.dashboardUrl, '_blank', 'noopener,noreferrer');
            toast.success(t('tracker.orders.openedStarshipitDashboard'));
            return;
          }
          if (result.labelBase64) {
            const printResult = await agentPrintLabel({
              data: result.labelBase64,
              format: result.labelType || 'PDF',
            });
            if (printResult.success) {
              toast.success(t('tracker.orders.shippingLabelSentToPrinter'));
            } else {
              toast.error(printResult.error ?? t('tracker.orders.shippingLabelPrintFailed'));
            }
            return;
          }
          toast.error(result.errorMessage ?? t('tracker.orders.shippingLabelPrintFailed'));
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  }, [locationId, reprintShippingLabelMutation, agentPrintLabel, t]);

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="space-y-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4 py-3.5 px-4 border-b last:border-0">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-8" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-8" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return null; // Parent handles empty state
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><span className="sr-only">{t('tracker.orders.header.expand')}</span></TableHead>
              <TableHead>{t('tracker.orders.header.order')}</TableHead>
              <TableHead>{t('tracker.orders.header.customer')}</TableHead>
              <TableHead>{t('tracker.orders.header.type')}</TableHead>
              <TableHead>{t('tracker.orders.header.batch')}</TableHead>
              <TableHead>{t('tracker.orders.header.bin')}</TableHead>
              <TableHead>{t('tracker.orders.header.age')}</TableHead>
              <TableHead>{t('tracker.orders.header.updated')}</TableHead>
              <TableHead className="text-right">{t('tracker.orders.header.items')}</TableHead>
              <TableHead>{t('tracker.orders.header.progress')}</TableHead>
              <TableHead>{t('tracker.orders.header.status')}</TableHead>
              <TableHead>{t('tracker.orders.header.tracking')}</TableHead>
              <TableHead className="w-10"><span className="sr-only">{t('tracker.orders.header.actions')}</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <OrderRowGroup
                key={order.id}
                order={order}
                isExpanded={expandedOrderId === order.id}
                onToggle={() => toggleExpand(order.id)}
                shopifyDomain={shopifyDomain}
                hasOverrideAccess={hasOverrideAccess}
                onOverrideStatus={handleOverrideStatus}
                locationId={locationId}
                invoiceEnabled={invoiceEnabled}
                onPrintOrderInvoice={onPrintOrderInvoice}
                onPutOnHold={onPutOnHold}
                onClientQcRedoDone={onClientQcRedoDone}
                canReprintShippingLabel={canReprintShippingLabel}
                onReprintShippingLabel={handleReprintShippingLabel}
                clientOrg={clientOrg}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {overrideTarget && (
        <StatusOverrideDialog
          open={!!overrideTarget}
          onOpenChange={(open) => { if (!open) setOverrideTarget(null); }}
          target={overrideTarget}
        />
      )}
    </>
  );
}
